import { useState, useEffect, useMemo } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import {
  addPatientAnalysisReportJob,
  updatePatientAnalysisReportJob,
  removePatientAnalysisReportJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getMinDate, getOneMonthMaxDate } from '../../../../utils/dateRangeValidation'

const PatientAnalysisReportAsync = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.patientAnalysisReportJobs || [])

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'patient_analysis_report_async',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      patient_type: 'cash',
      group_by: 'nationality',
      format: 'excel'
    }
  )

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addPatientAnalysisReportJob,
    updateJobAction: updatePatientAnalysisReportJob,
    removeJobAction: removePatientAnalysisReportJob,
    createEndpoint: '/v3/reports/patient-analysis/jobs',
    statusEndpoint: '/v3/reports/patient-analysis/jobs',
    downloadEndpoint: '/v3/reports/patient-analysis/jobs',
    fileNamePrefix: 'patient_analysis_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      patient_type: filters.patient_type || 'cash',
      group_by: filters.group_by || 'nationality',
      format
    })
  })

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
  }

  const handleChange = e => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'patient_type',
      label: 'Patient Type',
      options: [
        { label: 'Cash', value: 'cash' },
        { label: 'Insurance', value: 'insurance' },
        { label: 'All', value: 'all' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'group_by',
      label: 'Group By',
      options: [
        { label: 'Nationality Wise', value: 'nationality' },
        { label: 'Doctor Wise', value: 'doctor' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Patient Analysis Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Patient Analysis Report')}
      />
    </div>
  )
}

export default PatientAnalysisReportAsync